import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import {
  Button,
  Grid,
  Stack,
  SvgIcon,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { DraggableTag } from './DraggableTag';
import { tagListItem } from '../../config/pdfReportConfig';
import { AddTagDialog } from './AddTagDialog';
import { useToggle } from 'src/hooks/useToggle';
import { useFormContext } from 'react-hook-form';
import { useDebounce } from 'src/hooks/useDebounce';
import { useAuthentication } from 'src/features/authentication/context';
import { useEffect, useState } from 'react';
import { useGetSiteTags } from 'src/hooks/apiHooks/useGetSiteTags';
import { TagResponse } from 'src/types/tags/tags';
import { SitesService } from 'src/services/sites/sites.service';

type props = {
  onChange: (tagItem: tagListItem[]) => void;
  selectedSiteId: string;
  disabled?: boolean;
};

export const PDFReportAddTag = ({ onChange, selectedSiteId, disabled }: props) => {
  const [search, setSearch] = useState<string>('');
  const [isInitalLoadingDone, setIsInitalLoadingDone] = useState(false);
  const [allTags, setAllTags] = useState<TagResponse[] | null>(null);
  const { state, onToggle } = useToggle();
  const {
    customerId: { value: customerId },
  } = useAuthentication();
  const { watch } = useFormContext();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  const isLg = useMediaQuery(theme.breakpoints.only('lg'));
  const isXl = useMediaQuery(theme.breakpoints.only('xl'));

  const charLimit = () => {
    if (isXl) return 25;
    else if (isLg) return 20;
    else if (isMd) return 15;
    else if (isSm) return 25;
    else if (isXs) return 25;
    return 10;
  };
  const setLimit = charLimit();

  const tagList: tagListItem[] = watch('data.tagList') || [];

  const sensors = useSensors(useSensor(PointerSensor));
  useEffect(() => {
    if (!customerId || !selectedSiteId) {
      return;
    }
    const fetchData = async () => {
      try {
        const res = await SitesService.getTags(customerId, selectedSiteId);
        setAllTags(res);
      } catch (error) {
        console.error(error);
      } finally {
        setIsInitalLoadingDone(true);
      }
    };
    fetchData();
  }, [customerId, selectedSiteId]);

  const { tags, isLoading } = useGetSiteTags({
    customerId: customerId!,
    siteId: selectedSiteId,
    searchQuery: useDebounce(search, 200),
  });
  const tagDisplayNameMap =
    allTags?.reduce((acc, tag) => {
      acc[tag.id] = tag.displayName;
      return acc;
    }, {} as Record<string, string>) || {};

  return (
    <Box width="100%" padding={2}>
      <Stack gap="8px">
        <Typography variant="body1">Tags</Typography>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event) => {
            const { active, over } = event;
            if (active.id !== over?.id) {
              const oldIndex = tagList.findIndex((p) => p.inputId === active.id);
              const newIndex = tagList.findIndex((p) => p.inputId === over?.id);
              const newItems = arrayMove(tagList, oldIndex, newIndex);
              const orderedItems = newItems.map((item, index) => ({
                ...item,
                order: index,
              }));
              onChange(orderedItems);
            }
          }}
        >
          <SortableContext items={tagList.map((p) => p.inputId)} strategy={rectSortingStrategy}>
            <Grid container spacing={3}>
              {tagList.map((property, index: number) => (
                <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={property.inputId}>
                  <DraggableTag
                    item={property}
                    isLoading={!isInitalLoadingDone}
                    displayName={tagDisplayNameMap[property.inputId] ?? property.rawInputName}
                    charLimit={setLimit}
                    disabled={disabled}
                    onEdit={() => {
                      onToggle();
                    }}
                    onRemove={(data) => {
                      const updated = tagList
                        .filter((tag) => tag.inputId !== data.inputId)
                        .map((item, index) => ({
                          ...item,
                          order: index,
                        }));
                      onChange(updated);
                    }}
                    key={property.inputId}
                    index={index}
                  />
                </Grid>
              ))}
            </Grid>
          </SortableContext>
        </DndContext>

        <Stack flexDirection="row" justifyContent="flex-end">
          <Button
            onClick={() => {
              onToggle();
            }}
            disabled={!selectedSiteId || disabled}
            variant="text"
            color="primary"
            data-sm="add-columnt-btn"
          >
            <Stack flexDirection="row" gap="12px">
              <SvgIcon component={Add} />
              <Typography variant="body1">Add Tag</Typography>
            </Stack>
          </Button>
        </Stack>
        <AddTagDialog
          state={state}
          onToggle={onToggle}
          siteId={selectedSiteId}
          tagList={tagList}
          onChange={onChange}
          setSearch={setSearch}
          isLoading={isLoading}
          tags={tags}
        />
      </Stack>
    </Box>
  );
};
